import { useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getStatusBreakdown, getTopHosts, getVisitsByDay } from "./charts-api";
import type { StatusBreakdownPoint, TopHostPoint, VisitsByDayPoint } from "./charts-api";

// Validated for lightness/chroma/CVD-separation/contrast on a white surface
// (dataviz palette checks); identity is never color-alone -- the donut has
// per-slice gaps, a legend, and tooltips.
const STATUS_COLORS: Record<string, string> = {
  PENDING: "#d97706",
  INVITED: "#7c3aed",
  APPROVED: "#059669",
  INSIDE: "#0284c7",
  OVERSTAY: "#dc2626",
  CHECKED_OUT: "#64748b",
  REJECTED: "#991b1b",
};

function shortDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
}

export function ChartsPanel() {
  const [visitsByDay, setVisitsByDay] = useState<VisitsByDayPoint[] | null>(null);
  const [statusBreakdown, setStatusBreakdown] = useState<StatusBreakdownPoint[] | null>(null);
  const [topHosts, setTopHosts] = useState<TopHostPoint[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      getVisitsByDay(14, controller.signal),
      getStatusBreakdown(controller.signal),
      getTopHosts(5, controller.signal),
    ])
      .then(([byDay, breakdown, hosts]) => {
        setVisitsByDay(byDay);
        setStatusBreakdown(breakdown);
        setTopHosts(hosts);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted) return;
        setError(requestError instanceof Error ? requestError.message : "Failed to load charts");
      });

    return () => controller.abort();
  }, []);

  if (error) {
    return <p className="error-banner">{error}</p>;
  }

  return (
    <div className="chart-grid">
      <article className="panel chart-panel">
        <header className="panel-header">
          <h2>Visits (last 14 days)</h2>
        </header>
        {visitsByDay ? (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={visitsByDay}>
              <defs>
                <linearGradient id="visitsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ea580c" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#ea580c" stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip labelFormatter={(value) => shortDate(String(value))} />
              <Area type="monotone" dataKey="count" stroke="#ea580c" fill="url(#visitsGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="skeleton skeleton-card" style={{ height: 260 }} />
        )}
      </article>

      <article className="panel chart-panel">
        <header className="panel-header">
          <h2>Status Breakdown</h2>
        </header>
        {statusBreakdown ? (
          statusBreakdown.length === 0 ? (
            <p className="empty-card">No visits recorded yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={statusBreakdown}
                  dataKey="count"
                  nameKey="status"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={2}
                >
                  {statusBreakdown.map((entry) => (
                    <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || "#94a3b8"} />
                  ))}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )
        ) : (
          <div className="skeleton skeleton-card" style={{ height: 260 }} />
        )}
      </article>

      <article className="panel chart-panel">
        <header className="panel-header">
          <h2>Busiest Hosts</h2>
        </header>
        {topHosts ? (
          topHosts.length === 0 ? (
            <p className="empty-card">No visits recorded yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={topHosts} layout="vertical" margin={{ left: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="host_name" tick={{ fontSize: 12 }} width={110} />
                <Tooltip />
                <Bar dataKey="visit_count" fill="#ea580c" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )
        ) : (
          <div className="skeleton skeleton-card" style={{ height: 260 }} />
        )}
      </article>
    </div>
  );
}
